'use client';

import { bgPrimary } from '@osmosis/tailwind-ui/styles/background';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Route } from '../../../hooks/useRouterPush';

import React, { ReactNode } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { getAuthPath } from '../../../lib/composer/url';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import { text5, textBlue } from '@osmosis/tailwind-ui/styles/text';

type Props = {
  blurredBlockType?: string;
  hasDMTAccess?: boolean;
  sampleLinkText?: ReactNode;
  sampleLinkURL?: string;
  denyReason?: string | null;
  fullHeight?: boolean;
};

/**
 * BlurredBlock component.
 */
const BlurredBlock = ({
  blurredBlockType,
  hasDMTAccess,
  denyReason,
  sampleLinkURL = '/learn/Approach_to_chest_pain:_Clinical_practice',
  fullHeight = false,
}: Props) => {
  const pathName = usePathname() || '/';
  const searchParams = useSearchParams();
  const { getPlansUrl } = useUserContext();

  const intl = useIntl();

  if (hasDMTAccess) return null;

  if (
    (blurredBlockType === 'decision-making-tree' && !hasDMTAccess) ||
    denyReason === 'expired'
  ) {
    return (
      <Box
        data-testid={`blurred-block-dmt`}
        className={`absolute flex w-full flex-col items-center justify-center ${
          fullHeight ? 'top-0 h-full' : 'bottom-0 h-1/2'
        } left-0 z-10 rounded-b-xl backdrop-blur-xl`}
      >
        <P
          className={`max-w-md text-center text-base font-bold sm:text-lg ${text5} mb-4`}
        >
          <FormattedMessage
            id="learn.blurred_block.subscription_message"
            defaultMessage="Access more great content with an Osmosis subscription."
          />
        </P>
        <Flex className={`flex-row items-center justify-center gap-8`}>
          <Link href={getPlansUrl()}>
            <Button
              size="m"
              data-testid={`view_plans_button`}
              className={`${bgPrimary} mb-2 rounded-full px-8`}
            >
              <FormattedMessage
                id="learn.blurred_block.action.button"
                defaultMessage="View Plans"
              />
            </Button>
          </Link>
          <Button
            as={'a'}
            href={sampleLinkURL}
            variant={'transparent'}
            data-testid={`view_sample_button`}
            className={`${textBlue} cursor-pointer pb-2 font-bold underline transition-all duration-100 hover:opacity-70`}
          >
            <FormattedMessage
              id="learn.blurred_block.view_sample"
              defaultMessage="View Sample"
            />
          </Button>
        </Flex>
      </Box>
    );
  }

  let displayText = '';
  if (blurredBlockType === 'note') {
    displayText = intl.formatMessage({
      id: 'learn.blurred_block.note_message',
      defaultMessage:
        'Want to see more? Sign in or sign up to access the full notes and additional features.',
    });
  } else {
    displayText = intl.formatMessage({
      id: 'learn.blurred_block.product_message',
      defaultMessage:
        'Want to see more? Sign in or sign up to access the full product with additional features.',
    });
  }

  return (
    <Box
      data-testid={`blurred-block-${blurredBlockType}`}
      className={`absolute flex w-full flex-col items-center justify-center ${
        fullHeight ? 'top-0 h-full' : 'bottom-0 h-1/2'
      } left-0 z-10 rounded-b-xl backdrop-blur-xl`}
    >
      <P
        className={`max-w-md text-center text-base font-bold sm:text-lg ${text5} mb-4`}
      >
        {displayText}
      </P>
      <Flex className={`flex-row items-center justify-center gap-8`}>
        <Link href={getAuthPath(Route.CREATE, pathName, searchParams)}>
          <Button
            size="m"
            data-testid={`sign-up-button`}
            className={`${bgPrimary} mb-2 rounded-full px-8`}
          >
            <FormattedMessage
              id="learn.blurred_block.action.button"
              defaultMessage="Create a free account"
            />
          </Button>
        </Link>
        <Link href={getAuthPath(Route.LOGIN, pathName, searchParams)}>
          <Button
            data-testid={`log-in-button`}
            variant="outline"
            color="blue"
            className="rounded-full px-8"
          >
            <FormattedMessage id="common.signIn" defaultMessage="Sign in" />
          </Button>
        </Link>
      </Flex>
    </Box>
  );
};

export default BlurredBlock;
